import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { functions } from "../lib/firebase";
import { queryKeys } from "../lib/queryKeys";
import type { Icp, IcpCreate, IcpUpdate } from "../lib/types";

interface ListResponse {
  icps: Icp[];
}

interface UpdateResponse {
  icp: Icp;
}

const callList = httpsCallable<Record<string, never>, ListResponse>(
  functions,
  "cf_list_icps",
);

const callUpdate = httpsCallable<IcpUpdate, UpdateResponse>(
  functions,
  "cf_update_icp",
);

const callCreate = httpsCallable<IcpCreate, UpdateResponse>(
  functions,
  "cf_create_icp",
);

const callAutofillRegions = httpsCallable<
  { context: string },
  { regions: string[] }
>(functions, "cf_autofill_regions");

const callAutofillKeywords = httpsCallable<
  { context: string; companies: string[] },
  { keywords: string[] }
>(functions, "cf_autofill_keywords");

const callAutofillTitles = httpsCallable<
  { context: string },
  { titles: string[] }
>(functions, "cf_autofill_titles");

export function useIcps(options?: { includeInactive?: boolean }) {
  const includeInactive = options?.includeInactive ?? false;
  return useQuery<Icp[], Error, Icp[]>({
    queryKey: queryKeys.icps.all,
    queryFn: async () => {
      const res = await callList({});
      return res.data.icps;
    },
    staleTime: 60_000,
    select: (icps) =>
      includeInactive ? icps : icps.filter((i) => i.is_active),
  });
}

export function useUpdateIcp() {
  const qc = useQueryClient();
  return useMutation<Icp, Error, IcpUpdate>({
    mutationFn: async (body) => {
      const res = await callUpdate(body);
      return res.data.icp;
    },
    onSuccess: (icp) => {
      qc.setQueryData<Icp[]>(queryKeys.icps.all, (prev) =>
        prev ? prev.map((p) => (p.name === icp.name ? icp : p)) : prev,
      );
    },
  });
}

export function useCreateIcp() {
  const qc = useQueryClient();
  return useMutation<Icp, Error, IcpCreate>({
    mutationFn: async (body) => {
      const res = await callCreate(body);
      return res.data.icp;
    },
    onSuccess: (icp) => {
      qc.setQueryData<Icp[]>(queryKeys.icps.all, (prev) =>
        prev ? [...prev, icp] : prev,
      );
    },
  });
}

export function useAutofillRegions() {
  return useMutation<string[], Error, string>({
    mutationFn: async (context) => {
      const res = await callAutofillRegions({ context });
      return res.data.regions;
    },
  });
}

export function useAutofillKeywords() {
  return useMutation<string[], Error, { context: string; companies: string[] }>({
    mutationFn: async (body) => {
      const res = await callAutofillKeywords(body);
      return res.data.keywords;
    },
  });
}

export function useAutofillTitles() {
  return useMutation<string[], Error, string>({
    mutationFn: async (context) => {
      const res = await callAutofillTitles({ context });
      return res.data.titles;
    },
  });
}
